import React, { useEffect, useState } from 'react';
import { publicSupabaseClient } from '../../src/lib/publicSupabaseClient';
import { getSiteScriptsBySlot } from '../../services/supabaseService';
import { parseEmbedHtml } from '../../src/lib/utils/parseEmbedHtml';
import { useEmbedScripts } from '../../src/lib/hooks/useEmbedScripts';

interface CodeSlotProps {
  /** Which admin-configured content slot to render — see src/lib/contentSlotAnchors.ts. */
  slotKey: string;
  className?: string;
}

/**
 * Renders whatever admin-authored code (if any) is assigned to `slotKey` in
 * public.site_scripts — read live via the public Supabase client, since this
 * mounts inside client-rendered components (Footer, modals) that don't go
 * through api/index.js's SSR string-replace. Renders nothing at all (not a
 * placeholder) while unconfigured or still loading, so an un-assigned anchor
 * doesn't show a stub card to real visitors.
 *
 * Admin-pasted code can mix static markup and <script> tags in one blob
 * (e.g. a GoHighLevel inline form embed) — parseEmbedHtml splits them so the
 * scripts can be injected as real DOM nodes (useEmbedScripts), since
 * dangerouslySetInnerHTML never executes <script> tags.
 */
export const CodeSlot: React.FC<CodeSlotProps> = ({ slotKey, className = '' }) => {
  const [code, setCode] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSiteScriptsBySlot(publicSupabaseClient, slotKey).then((result) => {
      if (!cancelled) setCode(result);
    });
    return () => {
      cancelled = true;
    };
  }, [slotKey]);

  const { markup, scripts } = code ? parseEmbedHtml(code) : { markup: '', scripts: [] };
  useEmbedScripts(`code-slot-${slotKey}`, scripts);

  const isConfigured = Boolean(code && (markup || scripts.length > 0));
  if (!isConfigured) return null;

  return (
    <div className={`code-slot rounded-[1.5rem] border border-white/10 bg-white/5 backdrop-blur-md ${className}`}>
      <div dangerouslySetInnerHTML={{ __html: markup }} />
    </div>
  );
};
